import { userAddressService } from '@/services/userAddresses';
import { getCurrentUser, loadFromStorage } from './location-storage';
import type { LocationCoordinate, AddressInfo, SavedAddress } from './location.types';

/**
 * 로그인 사용자의 주소 목록을 데이터베이스에서 로드
 */
export const loadUserAddresses = async (userId: string) => {
  try {
    const result = await userAddressService.getUserAddresses(userId);
    
    if (result.success && result.data) {
      // DB 주소를 로컬 상태 형식으로 변환
      const addresses: SavedAddress[] = result.data.map(dbAddress => ({
        id: dbAddress.id,
        alias: dbAddress.alias,
        address: dbAddress.address,
        jibunAddress: dbAddress.address, // DB에는 도로명주소만 저장
        detailAddress: dbAddress.detail_address || '',
        deliveryInstructions: '', // DB에는 저장되지 않는 필드
        additionalInfo: '', // DB에는 저장되지 않는 필드
        coordinate: {
          latitude: dbAddress.latitude || 0,
          longitude: dbAddress.longitude || 0
        },
        isDefault: dbAddress.is_default,
        createdAt: dbAddress.created_at
      }));
      
      // 선택된 주소 찾기
      const selectedAddress = addresses.find(addr => addr.isDefault);
      const selectedAddressId = selectedAddress?.id || null;
      
      // currentLocation 설정
      let currentLocation: { coordinate: LocationCoordinate | null; address: AddressInfo | null } = { 
        coordinate: null, 
        address: null 
      };
      if (selectedAddress) {
        currentLocation = {
          coordinate: selectedAddress.coordinate,
          address: {
            roadAddress: selectedAddress.detailAddress 
              ? `${selectedAddress.address} ${selectedAddress.detailAddress}`
              : selectedAddress.address,
            jibunAddress: selectedAddress.detailAddress
              ? `${selectedAddress.jibunAddress} ${selectedAddress.detailAddress}`
              : selectedAddress.jibunAddress,
            zonecode: selectedAddress.additionalInfo
          }
        };
      }
      
      return {
        savedAddresses: addresses,
        selectedAddressId,
        currentLocation,
        error: null
      };
    } else {
      console.error('Failed to load user addresses:', result.error);
      return {
        savedAddresses: [],
        selectedAddressId: null,
        currentLocation: { coordinate: null, address: null },
        error: result.error || '주소 목록을 불러오는데 실패했습니다.'
      };
    }
  } catch (error) {
    console.error('Failed to load user addresses:', error);
    return {
      savedAddresses: [],
      selectedAddressId: null,
      currentLocation: { coordinate: null, address: null },
      error: '주소 목록을 불러오는데 실패했습니다.'
    };
  }
};

/**
 * 현재 인증 상태에 따라 적절한 데이터 소스에서 주소를 동기화
 */
export const syncWithDatabase = async () => {
  try {
    const user = await getCurrentUser();
    
    if (user) {
      // 로그인 사용자: DB에서 주소 로드
      return await loadUserAddresses(user.id);
    } else {
      // 게스트 사용자: localStorage에서 주소 로드
      const storageData = loadFromStorage();
      return {
        savedAddresses: storageData?.savedAddresses || [],
        selectedAddressId: storageData?.selectedAddressId || null,
        currentLocation: storageData?.currentLocation || { coordinate: null, address: null },
        error: null
      };
    }
  } catch (error) {
    console.error('Failed to sync with database:', error);
    // 실패 시 localStorage 폴백
    const storageData = loadFromStorage();
    return {
      savedAddresses: storageData?.savedAddresses || [],
      selectedAddressId: storageData?.selectedAddressId || null,
      currentLocation: storageData?.currentLocation || { coordinate: null, address: null },
      error: null
    };
  }
};

/**
 * 인증 상태를 확인하고 적절한 주소 데이터를 로드
 */
export const checkAuthAndLoad = async () => {
  try {
    return await syncWithDatabase();
  } catch (error) {
    console.error('Failed to check auth and load:', error);
    return {
      savedAddresses: [],
      selectedAddressId: null,
      currentLocation: { coordinate: null, address: null },
      error: '주소 데이터 로드에 실패했습니다.'
    };
  }
};